import { api } from "@/utils/api";
import type { JobWithProgress } from "@shared/types";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

interface FetchJobsOptions {
  page?: number;
  limit?: number;
  status?: string;
}

interface BulkReloadOptions {
  page?: number;
}

interface MassActionResult {
  message: string;
  removed?: number;
  stopped?: number;
}

export const useJobsStore = defineStore("jobs", () => {
  // State
  const jobs = ref<JobWithProgress[]>([]);
  const selectedJobId = ref<string | null>(null);
  const isLoading = ref(false);
  const currentRequestId = ref(0);
  const lastStatusFilter = ref<string | undefined>(undefined);
  const pagination = ref({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 1,
  });

  // Phase 20: Queue status (real-time via WebSocket)
  const queueStatus = ref<{
    queueLength: number;
    activeJobs: number;
    maxConcurrent: number;
    activeByUser: Record<number, number>;
    pendingByUser: Record<number, number>;
  }>({
    queueLength: 0,
    activeJobs: 0,
    maxConcurrent: 3,
    activeByUser: {},
    pendingByUser: {},
  });

  // Getters
  const selectedJob = computed(() =>
    jobs.value.find((j) => j.job_id === selectedJobId.value),
  );

  const pendingJobs = computed(() =>
    jobs.value.filter((j) => j.status === "pending"),
  );

  const processingJobs = computed(() =>
    jobs.value.filter((j) => j.status === "processing"),
  );

  const completedJobs = computed(() =>
    jobs.value.filter((j) => j.status === "completed"),
  );

  const failedJobs = computed(() =>
    jobs.value.filter((j) => j.status === "failed"),
  );

  const totalJobs = computed(() => jobs.value.length);

  const activeJobsCount = computed(() => processingJobs.value.length);

  const pendingJobsCount = computed(() => pendingJobs.value.length);

  // Actions
  /**
   * Update maxConcurrent from system settings
   */
  const updateMaxConcurrentJobs = (maxConcurrent: number): void => {
    queueStatus.value.maxConcurrent = maxConcurrent;
  };

  const fetchJobs = async (options?: string | FetchJobsOptions): Promise<void> => {
    const requestId = currentRequestId.value + 1;
    currentRequestId.value = requestId;
    isLoading.value = true;
    try {
      const normalizedOptions =
        typeof options === "string" ? { status: options } : options || {};
      if ("status" in normalizedOptions) {
        lastStatusFilter.value = normalizedOptions.status;
      }
      const params = {
        page: normalizedOptions.page ?? pagination.value.page,
        limit: normalizedOptions.limit ?? pagination.value.limit,
        ...(lastStatusFilter.value ? { status: lastStatusFilter.value } : {}),
      };
      const response = await api.get<{
        jobs: JobWithProgress[];
        pagination?: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
        };
      }>("/api/jobs", params);

      if (requestId !== currentRequestId.value) {
        return;
      }

      jobs.value = response.jobs;
      pagination.value = response.pagination || {
        page: params.page,
        limit: params.limit,
        total: response.jobs.length,
        totalPages: 1,
      };
    } catch (error: unknown) {
      if (requestId !== currentRequestId.value) {
        return;
      }
      console.error("[JobsStore] ❌ Failed to fetch jobs:", error);
      throw error;
    } finally {
      if (requestId === currentRequestId.value) {
        isLoading.value = false;
      }
    }
  };

  const reloadCurrentPage = async (page = pagination.value.page): Promise<void> => {
    await fetchJobs({
      page,
      limit: pagination.value.limit,
      status: lastStatusFilter.value,
    });

    if (
      pagination.value.total > 0 &&
      jobs.value.length === 0 &&
      pagination.value.page > 1
    ) {
      await fetchJobs({
        page: Math.max(1, pagination.value.totalPages),
        limit: pagination.value.limit,
        status: lastStatusFilter.value,
      });
    }
  };

  const fetchJobById = async (jobId: string): Promise<void> => {
    try {
      const job = await api.get<JobWithProgress>(`/api/jobs/${jobId}`);
      const index = jobs.value.findIndex((j) => j.job_id === jobId || j.id === jobId);
      if (index !== -1) {
        // Update existing job with fresh data (create new array for reactivity)
        jobs.value = jobs.value.map((j, i) => i === index ? job : j);
      } else {
        // Job not in list yet, add it
        jobs.value = [job, ...jobs.value];
      }
    } catch (error: unknown) {
      console.error("[JobsStore] ❌ Failed to fetch job by ID:", error);
      // If job not found (404), remove it from list
      if ((error as any)?.response?.status === 404) {
        removeJob(jobId);
      }
    }
  };

  const createJob = async (jobData: {
    search_url: string;
    search_params: Record<string, unknown>;
    config_id?: number;
    config_name?: string;
    vin?: string;
    custom_id?: string;
    taxation?: string;
    batch_list_id?: number;
    allowDuplicate?: boolean; // Phase 22: Allow intentional duplicate VIN jobs
  }): Promise<JobWithProgress> => {
    try {
      const newJob = await api.post<JobWithProgress>("/api/jobs", jobData);
      jobs.value.unshift(newJob);
      return newJob;
    } catch (error: unknown) {
      console.error("Failed to create job:", error);
      throw error;
    }
  };

  const deleteJob = async (jobId: string): Promise<void> => {
    try {
      await api.delete(`/api/jobs/${jobId}`);
      removeJob(jobId);
    } catch (error: unknown) {
      console.error("Failed to delete job:", error);
      throw error;
    }
  };

  const pauseJob = async (jobId: string): Promise<void> => {
    try {
      await api.patch(`/api/jobs/${jobId}/pause`);
      // WebSocket will update the job status, but update locally for immediate feedback
      updateJob(jobId, { status: "paused" });
    } catch (error: unknown) {
      console.error("Failed to pause job:", error);
      throw error;
    }
  };

  const resumeJob = async (jobId: string): Promise<void> => {
    try {
      await api.patch(`/api/jobs/${jobId}/resume`);
      // WebSocket will update the job status, but update locally for immediate feedback
      updateJob(jobId, { status: "pending" });
    } catch (error: unknown) {
      console.error("Failed to resume job:", error);
      throw error;
    }
  };

  const restartJob = async (jobId: string): Promise<void> => {
    try {
      // Optimistic update: Remove job immediately
      removeJob(jobId);
      const response = await api.post<{ jobId: string }>(`/api/jobs/${jobId}/restart`);
      if (response.jobId) {
        await fetchJobById(response.jobId);
      }
      // WebSocket will still update status/events
    } catch (error: unknown) {
      console.error("Failed to restart job:", error);
      // Re-fetch jobs to restore state on error
      await fetchJobs();
      throw error;
    }
  };

  const reorderJobs = async (jobIds: string[]): Promise<void> => {
    try {
      await api.post("/api/jobs/reorder", { jobIds });

      // Reorder local state to match new order
      const reorderedJobIds = new Set(jobIds);
      const orderedJobs = jobIds
        .map((id) => jobs.value.find((j) => j.job_id === id))
        .filter((job): job is JobWithProgress => job !== undefined);

      // Keep jobs that were NOT reordered (completed, failed, processing, etc.)
      const nonReorderedJobs = jobs.value.filter((j) => !reorderedJobIds.has(j.job_id));

      jobs.value = [...orderedJobs, ...nonReorderedJobs];

    } catch (error: unknown) {
      console.error("Failed to reorder jobs:", error);
      throw error;
    }
  };

  const addJob = (job: JobWithProgress): void => {
    jobs.value.unshift(job);
  };

  const updateJob = (jobId: string, updates: Partial<JobWithProgress>): void => {
    const index = jobs.value.findIndex((j) => j.job_id === jobId || j.id === jobId);
    if (index !== -1) {
      // Create a new array with the updated job (Vue reactivity)
      jobs.value = jobs.value.map((job, i) => {
        if (i === index) {
          return {
            ...job,
            ...updates,
            updated_at: new Date().toISOString(),
          };
        }
        return job;
      });

    } else {
      console.error(`[JobsStore] ❌ Job ${jobId.slice(0, 8)} NOT FOUND! Looking for:`, jobId);
      console.error(`[JobsStore] Available job IDs:`, jobs.value.map(j => ({
        id: j.id,
        job_id: j.job_id
      })));
    }
  };

  const removeJob = (jobId: string): void => {
    const index = jobs.value.findIndex((j) => j.job_id === jobId || j.id === jobId);
    if (index !== -1) {
      jobs.value.splice(index, 1);
    }
  };

  // WebSocket initialization (event handlers are registered globally in socket.ts)
  const initializeWebSocket = (): void => {
    // Kept for backward compatibility - actual handlers are in socket.ts
  };

  /**
   * Bulk delete multiple jobs
   */
  const bulkDeleteJobs = async (
    jobIds: string[],
    options?: BulkReloadOptions,
  ): Promise<void> => {
    try {
      await api.post("/api/jobs/bulk-delete", { jobIds });

      await reloadCurrentPage(options?.page);
    } catch (error) {
      console.error("Failed to bulk delete jobs:", error);
      throw error;
    }
  };

  /**
   * Bulk pause multiple jobs
   */
  const bulkPauseJobs = async (jobIds: string[]): Promise<void> => {
    try {
      await api.post("/api/jobs/bulk-pause", { jobIds });

      // Update local state
      for (const jobId of jobIds) {
        updateJob(jobId, { status: "paused" });
      }
    } catch (error) {
      console.error("Failed to bulk pause jobs:", error);
      throw error;
    }
  };

  /**
   * Bulk resume multiple jobs
   */
  const bulkResumeJobs = async (jobIds: string[]): Promise<void> => {
    try {
      await api.post("/api/jobs/bulk-resume", { jobIds });

      // Update local state
      for (const jobId of jobIds) {
        updateJob(jobId, { status: "pending" });
      }
    } catch (error) {
      console.error("Failed to bulk resume jobs:", error);
      throw error;
    }
  };

  /**
   * Bulk restart multiple jobs (rescrape)
   */
  const bulkRestartJobs = async (jobIds: string[]): Promise<void> => {
    try {
      const response = await api.post<{ jobIds: string[] }>("/api/jobs/bulk-restart", { jobIds });

      // Remove old jobs from Queue view (new jobs will arrive via WebSocket)
      for (const jobId of jobIds) {
        removeJob(jobId);
      }

      if (response.jobIds?.length) {
        await Promise.all(response.jobIds.map((id) => fetchJobById(id)));
      }
      await reloadCurrentPage();
    } catch (error) {
      console.error("Failed to bulk restart jobs:", error);
      throw error;
    }
  };

  /**
   * Bulk cancel multiple jobs
   */
  const bulkCancelJobs = async (jobIds: string[]): Promise<void> => {
    try {
      await api.post("/api/jobs/bulk-cancel", { jobIds });

      // Update local state
      for (const jobId of jobIds) {
        updateJob(jobId, { status: "cancelled" });
      }
      await reloadCurrentPage();
    } catch (error) {
      console.error("Failed to bulk cancel jobs:", error);
      throw error;
    }
  };

  const removeFinishedJobs = async (): Promise<number> => {
    const response = await api.post<MassActionResult>("/api/jobs/remove-finished", {});
    await reloadCurrentPage(1);
    return response.removed ?? 0;
  };

  const stopOpenJobs = async (): Promise<number> => {
    const response = await api.post<MassActionResult>("/api/jobs/stop-open", {});
    await reloadCurrentPage();
    return response.stopped ?? 0;
  };

  return {
    jobs,
    selectedJobId,
    isLoading,
    loading: isLoading, // Alias for template usage
    pagination,
    queueStatus, // Phase 20: Queue stats
    selectedJob,
    pendingJobs,
    processingJobs,
    completedJobs,
    failedJobs,
    totalJobs,
    activeJobsCount,
    pendingJobsCount,
    fetchJobs,
    fetchJobById,
    createJob,
    deleteJob,
    pauseJob,
    resumeJob,
    restartJob,
    reorderJobs,
    bulkDeleteJobs,
    bulkPauseJobs,
    bulkResumeJobs,
    bulkRestartJobs,
    bulkCancelJobs,
    removeFinishedJobs,
    stopOpenJobs,
    addJob,
    updateJob,
    removeJob,
    initializeWebSocket,
    updateMaxConcurrentJobs,
  };
});
